import React, { useState, useEffect } from 'react';
import LocationMapModal from '../LocationMapModal';
import { useTheme } from '../themeControl/ThemeProvider';
import { getSessionUUID } from '../lib/AuthToken';
import { ActivityIndicator } from './ActivityIndicator';

const TimeSheets = ({ theme }) => {
    const [clockEvents, setClockEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showMap, setShowMap] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);

    // Helper function to format date for display
    const formatDateForDisplay = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB'); // This gives dd/mm/yyyy format
    };

    useEffect(() => {
        fetchClockEvents();
    }, [currentDate]);

    const fetchClockEvents = async () => {
        setLoading(true);
        try {
            const sessionToken = await getSessionUUID();
            const today = new Date().toISOString().split('T')[0];
            
            let response;
            if (currentDate === today) {
                // Use today endpoint for today's date
                response = await fetch('/timesheets/clock-events/today', {
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`
                    }
                });
            } else {
                // Use range endpoint for other dates
                response = await fetch('/timesheets/clock-events/range', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${sessionToken}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        start_date: currentDate,
                        end_date: currentDate
                    })
                });
            }

            if (response.ok) {
                const data = await response.json();
                setClockEvents(data.data || []);
            } else {
                console.error('Failed to fetch clock events');
                setClockEvents([]);
            }
        } catch (error) {
            console.error('Error fetching clock events:', error);
            setClockEvents([]);
        } finally {
            setLoading(false);
        }
    };

    const handleLocationClick = (event) => {
        setSelectedEvent(event);
        setShowMap(true);
    };

    const getEventTypeStyle = (eventType) => {
        const isDark = theme === 'dark';
        switch (eventType?.toLowerCase()) {
            case 'clock_in':
                return { 
                    backgroundColor: isDark ? '#0f5132' : '#d4edda', 
                    color: isDark ? '#d1e7dd' : '#155724', 
                    border: `1px solid ${isDark ? '#146c43' : '#c3e6cb'}` 
                };
            case 'clock_out':
                return { 
                    backgroundColor: isDark ? '#58151c' : '#f8d7da', 
                    color: isDark ? '#f5c2c7' : '#721c24', 
                    border: `1px solid ${isDark ? '#842029' : '#f5c6cb'}` 
                };
            case 'break_start':
                return { 
                    backgroundColor: isDark ? '#664d03' : '#fff3cd', 
                    color: isDark ? '#ffecb5' : '#856404', 
                    border: `1px solid ${isDark ? '#997404' : '#ffeaa7'}` 
                };
            case 'break_end':
                return { 
                    backgroundColor: isDark ? '#055160' : '#d1ecf1', 
                    color: isDark ? '#b6effb' : '#0c5460', 
                    border: `1px solid ${isDark ? '#0a58ca' : '#bee5eb'}` 
                };
            default:
                return { 
                    backgroundColor: isDark ? '#333' : '#f8f9fa', 
                    color: isDark ? '#fff' : '#333',
                    border: `1px solid ${isDark ? '#555' : '#dee2e6'}` 
                };
        }
    };

    const getLocationStatus = (event) => {
        const isDark = theme === 'dark';
        if (event.night_out) return { 
            text: 'Night Out', 
            color: isDark ? '#c29ffa' : '#6f42c1' 
        };
        if (event.off_site) return { 
            text: 'Off Site', 
            color: isDark ? '#ffad74' : '#fd7e14' 
        };
        if (event.in_vehicle) return { 
            text: 'In Vehicle', 
            color: isDark ? '#75dd99' : '#20c997' 
        };
        if (event.depot_id) return { 
            text: 'In Depot', 
            color: isDark ? '#6ea8fe' : '#0d6efd' 
        };
        return { 
            text: 'Unknown', 
            color: isDark ? '#adb5bd' : '#6c757d' 
        };
    };

    const groupEventsByDriver = (events) => {
        const grouped = {};
        events.forEach(event => {
            if (!grouped[event.user_id]) {
                grouped[event.user_id] = {
                    driver_name: event.user_name || event.user_id,
                    events: []
                };
            }
            grouped[event.user_id].events.push(event);
        });
        
        // Sort events by timestamp for each driver
        Object.keys(grouped).forEach(driverId => {
            grouped[driverId].events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        });
        
        return grouped;
    };

    const groupedEvents = groupEventsByDriver(clockEvents);

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <h3 style={{ color: theme === 'dark' ? '#fff' : '#333', marginBottom: '10px' }}>
                    Driver Time Sheets
                </h3>
                <p style={{ color: theme === 'dark' ? '#ccc' : '#666', fontSize: '16px' }}>
                    View driver clock-in/out events and locations for today
                </p>
            </div>

            {/* Date Selector */}
            <div style={{ 
                marginBottom: '20px', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: '10px' 
            }}>
                <label style={{ 
                    color: theme === 'dark' ? '#fff' : '#333',
                    fontWeight: 'bold' 
                }}>
                    Date:
                </label>
                <input
                    type="date"
                    value={currentDate}
                    onChange={(e) => setCurrentDate(e.target.value)}
                    style={{
                        padding: '8px 12px',
                        border: `1px solid ${theme === 'dark' ? '#555' : '#ddd'}`,
                        borderRadius: '4px',
                        backgroundColor: theme === 'dark' ? '#333' : '#fff',
                        color: theme === 'dark' ? '#fff' : '#333',
                        fontSize: '14px'
                    }}
                />
                <button
                    onClick={fetchClockEvents}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#1997eb',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                    }}
                    disabled={loading}
                >
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
                    <ActivityIndicator />
                </div>
            ) : Object.keys(groupedEvents).length === 0 ? (
                <div style={{ 
                    textAlign: 'center', 
                    color: theme === 'dark' ? '#ccc' : '#666',
                    fontSize: '18px',
                    marginTop: '50px'
                }}>
                    No clock events found for {new Date(currentDate).toLocaleDateString('en-GB')}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {Object.keys(groupedEvents).map(driverId => {
                        const driverData = groupedEvents[driverId];
                        return (
                            <div
                                key={driverId}
                                style={{
                                    backgroundColor: theme === 'dark' ? '#2a2a2a' : '#f8f9fa',
                                    border: `1px solid ${theme === 'dark' ? '#555' : '#ddd'}`,
                                    borderRadius: '12px',
                                    padding: '20px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                {/* Driver Header */}
                                <div style={{
                                    borderBottom: `1px solid ${theme === 'dark' ? '#555' : '#ddd'}`,
                                    paddingBottom: '10px',
                                    marginBottom: '15px'
                                }}>
                                    <h4 style={{
                                        margin: 0,
                                        color: theme === 'dark' ? '#fff' : '#333',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        👤 {driverData.driver_name}
                                        <span style={{
                                            fontSize: '14px',
                                            color: theme === 'dark' ? '#ccc' : '#666',
                                            fontWeight: 'normal'
                                        }}>
                                            {driverData.events.length} events
                                        </span>
                                    </h4>
                                </div>

                                {/* Events List */}
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {driverData.events.map((event, index) => {
                                        const locationStatus = getLocationStatus(event);
                                        const eventStyle = getEventTypeStyle(event.event_type);
                                        
                                        return (
                                            <div
                                                key={event.id}
                                                style={{
                                                    ...eventStyle,
                                                    padding: '12px',
                                                    borderRadius: '8px',
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {/* Main event data in grid */}
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '120px 150px 150px 200px 120px 1fr auto',
                                                    gap: '15px',
                                                    alignItems: 'center'
                                                }}>
                                                    <div>
                                                        <strong>
                                                            {new Date(event.timestamp).toLocaleTimeString('en-GB', {
                                                                hour: '2-digit',
                                                                minute: '2-digit'
                                                            })}
                                                        </strong>
                                                        <span style={{
                                                            fontSize: '11px',
                                                            color: theme === 'dark' ? '#ccc' : '#666',
                                                            marginLeft: '8px'
                                                        }}>
                                                            {formatDateForDisplay(event.date)}
                                                        </span>
                                                    </div>

                                                    <div style={{
                                                        textTransform: 'capitalize',
                                                        fontWeight: 'bold'
                                                    }}>
                                                        {event.event_type?.replace('_', ' ')}
                                                    </div>

                                                    {/* Night Out and Moffett Status */}
                                                    <div style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: '2px',
                                                        fontSize: '11px'
                                                    }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}>
                                                            <span>🌙</span>
                                                            <span style={{
                                                                color: event.night_out 
                                                                    ? (theme === 'dark' ? '#ffd43b' : '#856404')
                                                                    : (theme === 'dark' ? '#666' : '#adb5bd'),
                                                                fontWeight: event.night_out ? 'bold' : 'normal'
                                                            }}>
                                                                {event.night_out ? 'Night Out' : 'No'}
                                                            </span>
                                                        </div>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}>
                                                            <span>🔧</span>
                                                            <span style={{
                                                                color: event.used_moffett 
                                                                    ? (theme === 'dark' ? '#74c0fc' : '#0d6efd')
                                                                    : (theme === 'dark' ? '#666' : '#adb5bd'),
                                                                fontWeight: event.used_moffett ? 'bold' : 'normal'
                                                            }}>
                                                                {event.used_moffett ? 'Moffett Used' : 'No'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '5px'
                                                    }}>
                                                        <span style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            backgroundColor: locationStatus.color,
                                                            display: 'inline-block'
                                                        }}></span>
                                                        <span style={{ color: locationStatus.color, fontWeight: 'bold' }}>
                                                            {locationStatus.text}
                                                        </span>
                                                    </div>

                                                    <div style={{ 
                                                        fontSize: '12px', 
                                                        color: theme === 'dark' ? '#bbb' : '#666',
                                                        fontWeight: '500' 
                                                    }}>
                                                        {event.accuracy && `±${Math.round(event.accuracy)}m`}
                                                    </div>

                                                    <div style={{
                                                        fontSize: '12px',
                                                        color: theme === 'dark' ? '#ccc' : '#666',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {event.address || 'No address available'}
                                                    </div>

                                                    <button
                                                        onClick={() => handleLocationClick(event)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            backgroundColor: '#1997eb',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '12px',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                        disabled={!event.lat || !event.lng}
                                                    >
                                                        📍 View Map
                                                    </button>
                                                </div>
                                                
                                                {/* Driver Comments - full width underneath within the same container */}
                                                {event.event_type === 'clock_out' && event.driver_comments && (
                                                    <div style={{
                                                        marginTop: '8px',
                                                        padding: '6px 12px',
                                                        fontSize: '11px',
                                                        color: theme === 'dark' ? '#ffd43b' : '#856404',
                                                        fontWeight: 'bold',
                                                        backgroundColor: theme === 'dark' ? 'rgba(255, 212, 59, 0.1)' : 'rgba(133, 100, 4, 0.1)',
                                                        borderRadius: '4px',
                                                        border: `1px solid ${theme === 'dark' ? '#ffd43b40' : '#85640440'}`,
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'pre-wrap'
                                                    }}>
                                                        <span style={{ marginRight: '6px' }}>💬</span>
                                                        <strong>Driver Comment:</strong> {event.driver_comments}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Map Modal */}
            {showMap && selectedEvent && (
                <LocationMapModal
                    show={showMap}
                    onHide={() => {
                        setShowMap(false);
                        setSelectedEvent(null);
                    }}
                    location={{
                        lat: selectedEvent.lat,
                        lng: selectedEvent.lng,
                        address: selectedEvent.address,
                        accuracy: selectedEvent.accuracy,
                        timestamp: selectedEvent.timestamp,
                        event_type: selectedEvent.event_type
                    }}
                    title={`${selectedEvent.user_name} - ${selectedEvent.event_type}`}
                />
            )}
        </div>
    );
};

export default TimeSheets;